import { loadGpxFromFile, loadGpxFromUrl } from "./gpx.js";
import { createMapProvider } from "./map.js";
import { interpolatePoint } from "./profile.js";
import { MAX_GPX_BYTES, debounce, formatDistance, formatDistanceValue, formatDuration, formatElevation, formatSpeed } from "./utils.js";
import { renderProfile } from "./chart.js";

export function bootInstance(root) {
  const mapEl = root.querySelector(".bhgpxv__map");
  const mapContainer = root.querySelector(".bhgpxv__mapContainer");
  const controls = root.querySelector(".bhgpxv__controls");
  const gpxInput = root.querySelector(".bhgpxv__gpxInput");
  const dropzone = controls.querySelector(".bhgpxv__dropzone");
  const status = root.querySelector(".bhgpxv__status");
  const stats = root.querySelector(".bhgpxv__stats");
  const kmStats = root.querySelector(".bhgpxv__kmStats");
  const chartWrap = root.querySelector(".bhgpxv__chartWrap");
  const empty = root.querySelector(".bhgpxv__empty");
  const chart = root.querySelector(".bhgpxv__chart");
  const height = root.dataset.height || "600px";

  if (!mapEl) {
    return;
  }

  mapEl.style.height = height;
  if (mapContainer) {
    mapContainer.style.height = height;
  }

  let mapProvider = null;
  let currentProfile = null;
  let currentTrackLayer = null;
  let currentHighlightLayer = null;
  let currentChartApi = null;

  initialize();

  async function initialize() {
    try {
      setStatus(root.dataset.mapProvider === "google" ? "Loading Google Map..." : "Loading map...", false);
      mapProvider = await createMapProvider(mapEl, root);
      bindControls();

      const initialGpxUrl = getInitialGpxUrl(root);
      if (initialGpxUrl) {
        loadGPXFromUrl(initialGpxUrl);
        return;
      }

      setStatus("Upload a GPX file to render the viewer.", false);
    } catch (error) {
      setStatus(error && error.message ? error.message : "Unable to initialize the map.", true);
      console.error(error);
    }
  }

  function bindControls() {
    gpxInput.addEventListener("change", (event) => {
      const file = event.target.files && event.target.files[0];
      if (file) {
        loadGPXFromFile(file);
      }
      event.target.value = "";
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.add("dragover");
      });
    });

    ["dragleave", "drop"].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.remove("dragover");
      });
    });

    dropzone.addEventListener("drop", (event) => {
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) {
        loadGPXFromFile(file);
      }
    });

    window.addEventListener("resize", debounce(() => {
      if (currentProfile && currentProfile.hasElevation) {
        currentChartApi = renderProfile(currentProfile, root, chartWrap, chart, {
          onHover: showPosition,
          onLeave: clearPosition,
        });
      }
    }, 150));
  }

  async function loadGPXFromFile(file) {
    if (!file.name.toLowerCase().endsWith(".gpx")) {
      setStatus("Please upload a .gpx file.", true);
      trackGpxViewerEvent("GPX Viewer Error", file.name, {
        error_message: "Please upload a .gpx file.",
      });
      return;
    }

    if (file.size > MAX_GPX_BYTES) {
      setStatus("The GPX file is too large.", true);
      trackGpxViewerEvent("GPX Viewer Error", file.name, {
        error_message: "The GPX file is too large.",
      });
      return;
    }

    await loadGPX(() => loadGpxFromFile(file), "Failed to load GPX: ", file.name, true);
  }

  async function loadGPXFromUrl(url) {
    await loadGPX(() => loadGpxFromUrl(url), "Failed to load GPX from URL: ", getGpxFileName(url), false);
  }

  async function loadGPX(load, errorPrefix, gpxFileName, isUpload) {
    clearCurrentGpx();
    clearStats();
    setStatus("Reading GPX data...", false);

    try {
      currentProfile = await load();
      currentTrackLayer = mapProvider.addTrack(currentProfile, {
        onHover: showPosition,
        onLeave: clearPosition,
      });

      await mapProvider.fitBounds(mapProvider.getBounds(currentTrackLayer));

      updateStats(currentProfile);
      renderKmStats(currentProfile);
      renderElevationChart(currentProfile);
      stats.hidden = false;
      kmStats.hidden = !currentProfile.kmSplits.length;
      setStatus(`Loaded: ${formatDistance(currentProfile.totalDistance)}`, false);
      if (isUpload) {
        trackGpxViewerEvent("GPX Viewer Upload", gpxFileName, {
          has_elevation: currentProfile.hasElevation,
          has_time: currentProfile.hasTime,
          map_provider: root.dataset.mapProvider || "leaflet",
        });
      }
    } catch (error) {
      currentProfile = null;
      const errorMessage = error && error.message ? error.message : "Unable to render the GPX viewer.";
      setStatus(errorPrefix + errorMessage, true);
      trackGpxViewerEvent("GPX Viewer Error", gpxFileName, {
        error_message: errorMessage,
        map_provider: root.dataset.mapProvider || "leaflet",
      });
      console.error(error);
    }
  }

  function clearCurrentGpx() {
    clearPosition();
    clearSplitHighlight();

    if (currentTrackLayer) {
      mapProvider.removeLayer(currentTrackLayer);
      currentTrackLayer = null;
    }

    currentProfile = null;
    currentChartApi = null;
    chart.innerHTML = "";
    chartWrap.classList.remove("has-chart");
    empty.textContent = "Elevation profile will appear here.";
  }

  function clearStats() {
    stats.hidden = true;
    stats.querySelectorAll("[data-stat]").forEach((stat) => {
      stat.textContent = "-";
    });
    kmStats.hidden = true;
    kmStats.innerHTML = "";
  }

  function updateStats(profile) {
    stats.querySelector('[data-stat="distance"]').textContent = formatDistance(profile.totalDistance);
    stats.querySelector('[data-stat="time"]').textContent = profile.hasTime ? formatDuration(profile.totalSeconds) : "N/A";
    stats.querySelector('[data-stat="average-speed"]').textContent = profile.hasTime ? formatSpeed(profile.averageSpeed) : "N/A";
    stats.querySelector('[data-stat="ascent"]').textContent = profile.hasElevation ? formatElevation(profile.totalAscent) : "N/A";
    stats.querySelector('[data-stat="descent"]').textContent = profile.hasElevation ? formatElevation(profile.totalDescent) : "N/A";
    stats.querySelector('[data-stat="min"]').textContent = profile.hasElevation ? formatElevation(profile.minElevation) : "N/A";
    stats.querySelector('[data-stat="max"]').textContent = profile.hasElevation ? formatElevation(profile.maxElevation) : "N/A";
  }

  function renderElevationChart(profile) {
    if (!profile.hasElevation) {
      currentChartApi = null;
      chart.innerHTML = "";
      chartWrap.classList.remove("has-chart");
      empty.textContent = "Elevation profile is unavailable because this GPX has no elevation data.";
      return;
    }

    currentChartApi = renderProfile(profile, root, chartWrap, chart, {
      onHover: showPosition,
      onLeave: clearPosition,
    });
  }

  function renderKmStats(profile) {
    kmStats.innerHTML = profile.kmSplits.map((split) => `
      <button class="bhgpxv__kmStat" type="button" aria-label="${formatSplitRange(split)}">
        <span>${formatSplitRange(split)}</span>
        ${formatSplitDetails(profile, split)}
      </button>
    `).join("");

    kmStats.querySelectorAll(".bhgpxv__kmStat").forEach((stat, index) => {
      const split = profile.kmSplits[index];
      stat.addEventListener("mouseenter", () => highlightSplit(split));
      stat.addEventListener("focus", () => highlightSplit(split));
      stat.addEventListener("mouseleave", clearSplitHighlight);
      stat.addEventListener("blur", clearSplitHighlight);
    });
  }

  function highlightSplit(split) {
    clearSplitHighlight();

    if (!split || !split.pathSegments.length) {
      return;
    }

    currentHighlightLayer = mapProvider.createHighlight(split);
    if (currentChartApi) {
      currentChartApi.setRange(split.startDistance, split.endDistance);
    }
    showPosition((split.startDistance + split.endDistance) / 2);
  }

  function clearSplitHighlight() {
    if (currentHighlightLayer) {
      mapProvider.removeLayer(currentHighlightLayer);
      currentHighlightLayer = null;
    }

    if (currentChartApi) {
      currentChartApi.clearRange();
    }
    clearPosition();
  }

  function showPosition(distance) {
    if (!currentProfile) {
      return;
    }

    const point = interpolatePoint(currentProfile.points, distance);
    mapProvider.showCursor(point);

    if (currentChartApi) {
      currentChartApi.setCursorDistance(distance);
    }
  }

  function clearPosition() {
    if (mapProvider) {
      mapProvider.clearCursor();
    }

    if (currentChartApi) {
      currentChartApi.clearCursor();
    }
  }

  function setStatus(message, isError) {
    status.textContent = message;
    status.classList.toggle("bhgpxv__status--error", Boolean(isError));
  }

  function formatSplitRange(split) {
    return `${formatDistanceValue(split.startDistance)}-${formatDistanceValue(split.endDistance)} km`;
  }

  function formatSplitDetails(profile, split) {
    const details = [];

    if (profile.hasTime) {
      details.push(`<strong>${formatSpeed(split.averageSpeed)}</strong>`);
      details.push(`<em>${formatDuration(split.seconds)}</em>`);
    }

    if (profile.hasElevation) {
      details.push(`<small>${formatElevation(split.totalAscent)} up / ${formatElevation(split.totalDescent)} down</small>`);
    }

    if (!details.length) {
      details.push("<small>Distance only</small>");
    }

    return details.join("");
  }
}

function getInitialGpxUrl(root) {
  return root.dataset.gpxUrl || new URLSearchParams(window.location.search).get("gpx");
}

function getGpxFileName(urlValue) {
  if (!urlValue) {
    return "unknown.gpx";
  }

  try {
    const url = new URL(urlValue, window.location.origin);
    const fileName = url.pathname.split("/").filter(Boolean).pop();
    return fileName || "unknown.gpx";
  } catch (error) {
    return String(urlValue).split("/").filter(Boolean).pop() || "unknown.gpx";
  }
}

function trackGpxViewerEvent(action, fileName, params = {}) {
  if (typeof window.gtag !== "function") {
    return;
  }

  window.gtag("event", action, Object.assign({
    event_category: "GPX Viewer",
    event_label: fileName || "unknown.gpx",
  }, params));
}
